import { Outlet, createFileRoute, redirect } from "@tanstack/react-router";
import { AppShell, CommandPalette } from "@/components/ui";
import { ServiceWorkerRegister } from "@/components/service-worker-register";
import { useAuthStore } from "@/lib/auth-store";
import { waitForAuthHydration } from "@/lib/auth-hydration";

/**
 * Pathless layout `/_authed-tw` · GEMELO de `/_authed.tsx` (mismo guard de
 * auth, copiado tal cual) pero con el chrome del portal ya migrado a
 * Tailwind (`ui/app-shell`, `ui/command-palette-tw`) en vez de Mantine.
 *
 * Por qué un grupo pathless nuevo y no editar `_authed.tsx` in-place: ese
 * archivo es compartido por TODAS las rutas autenticadas (admin/*,
 * solicitudes/*, autorizacion/*, perfil, etc.) — cambiarlo ahí habría
 * afectado a todo el portal de una sola vez, no solo al piloto de esta
 * etapa (Login + Dashboard). Al ser AMBOS pathless (prefijo `_`), no
 * aparecen en la URL — `_authed-tw/dashboard.tsx` resuelve exactamente a
 * `/dashboard`, sin colisión con ninguna otra ruta.
 *
 * Etapas futuras (6/7/8) van moviendo el resto de `_authed/**` a este
 * grupo conforme se migra cada pantalla; cuando `_authed/` quede vacío se
 * borra junto con el AppShell/CommandPalette de Mantine (Etapa 9).
 */
export const Route = createFileRoute("/_authed-tw")({
  beforeLoad: async ({ location }) => {
    await waitForAuthHydration();
    const { accessToken } = useAuthStore.getState();
    if (!accessToken) {
      throw redirect({
        to: "/login",
        search: { redirect: location.href } as never,
      });
    }
  },
  component: AuthedTailwindLayout,
  // El shell NO usa el `defaultPendingComponent` (PageSkeleton): mientras espera la
  // hidratación de la sesión no hay chrome que mostrar, y un skeleton de página a
  // pantalla completa parpadearía antes del shell. Las rutas hijas sí lo usan.
  pendingComponent: () => null,
});

function AuthedTailwindLayout() {
  return (
    <AppShell>
      <Outlet />
      <CommandPalette />
      <ServiceWorkerRegister />
    </AppShell>
  );
}
